import React from "react";
import { styled } from "@mui/material/styles";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import { Box, Divider } from "@mui/material";

export interface PanelItem {
  url?: React.ReactNode;
  [key: string]: unknown;
}

export interface Panel {
  id: string | number;
  name: React.ReactNode;
  panels: PanelItem[];
  [key: string]: unknown;
}

interface Props {
  panelData: Panel[];
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-of-type)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
  "&.MuiAccordion-root": {
    border: "none",
  },
  "&.Mui-expanded:last-of-type": {
    marginBottom: theme.spacing(2),
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    sx={{ padding: 0, minHeight: "32px" }}
    expandIcon={
      <ExpandMoreIcon
        sx={{ fontSize: "2.4rem", transform: "rotate(270deg)" }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper": {
    alignSelf: "flex-start",
    marginTop: "4px",
  },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    margin: "8px",
    minWidth: 0,
    alignItems: "flex-start",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  borderTop: "1px solid rgba(0, 0, 0, .125)",
  overflow: "hidden",
}));

const SbuxCollapsiblePanel = ({ panelData }: Props) => {
  return (
    <Box>
      {panelData &&
        panelData.map((panel, index) => {
          return (
            <div key={index}>
              <Accordion>
                <AccordionSummary
                  aria-controls={`${panel.id}-content`}
                  id={`${panel.id}-header`}
                >
                  <CircleIcon
                    sx={{
                      color: "rgb(0, 98, 65)",
                      fontSize: 14,
                      marginTop: "3px",
                      marginLeft: -1,
                      marginRight: 0.8,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 18,
                      paddingBottom: 1,
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      display: "block",
                    }}
                  >
                    {panel.name}
                  </span>
                </AccordionSummary>
                <AccordionDetails>
                  {panel.panels.length > 0 &&
                    panel.panels.map((item, idx) => (
                      <Box
                        sx={{
                          flex: 1,
                          fontSize: 14,
                          marginLeft: 4,
                          marginTop: -1,
                          marginBottom: -1.8,
                          paddingTop: 0.5,
                          paddingBottom:
                            panel.panels.length - 1 !== idx ? 2 : 0,
                        }}
                        key={idx}
                      >
                        {item.url}
                      </Box>
                    ))}
                </AccordionDetails>
              </Accordion>
              <Divider />
            </div>
          );
        })}
    </Box>
  );
};

export default SbuxCollapsiblePanel;
